import { useEffect } from "react";
import { useLang } from "@/context/LangContext";

const bodyClass = "text-base leading-8 text-charcoal-400 md:text-lg";
const listClass = "mt-4 list-disc space-y-2 pl-5 text-base leading-8 text-charcoal-400 marker:text-terracotta md:text-lg";

function usePrivacyMetadata(content) {
  useEffect(() => {
    const previousTitle = document.title;
    const canonical = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute("href");
    const fields = [
      ['meta[name="description"]', content.meta_description],
      ['meta[property="og:title"]', content.meta_title],
      ['meta[property="og:description"]', content.meta_description],
      ['meta[property="og:url"]', "https://khalfanoman.com/privacy"],
      ['meta[name="twitter:title"]', content.meta_title],
      ['meta[name="twitter:description"]', content.meta_description],
    ];
    const previousFields = fields.map(([selector, value]) => {
      const element = document.querySelector(selector);
      const previousValue = element?.getAttribute("content");
      element?.setAttribute("content", value);
      return [element, previousValue];
    });

    document.title = content.meta_title;
    canonical?.setAttribute("href", "https://khalfanoman.com/privacy");

    return () => {
      document.title = previousTitle;
      if (canonical && previousCanonical) canonical.setAttribute("href", previousCanonical);
      previousFields.forEach(([element, previousValue]) => {
        if (!element) return;
        if (previousValue === null) element.removeAttribute("content");
        else element.setAttribute("content", previousValue);
      });
    };
  }, [content.meta_description, content.meta_title]);
}

function PolicySection({ number, title, children }) {
  const headingId = `privacy-section-${number}`;
  return (
    <section aria-labelledby={headingId} className="grid gap-5 border-t border-charcoal/15 pt-10 md:grid-cols-[3.5rem_minmax(0,1fr)] md:gap-8 md:pt-14">
      <p className="text-xs tracking-[0.25em] text-terracotta" aria-hidden="true">{String(number).padStart(2, "0")}</p>
      <div>
        <h2 id={headingId} className="font-serif-display text-3xl font-light leading-tight text-charcoal md:text-4xl">
          {title}
        </h2>
        <div className="mt-5 space-y-5">{children}</div>
      </div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className={listClass}>
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

function EmailLink({ email }) {
  return (
    <a
      href={`mailto:${email}`}
      className="font-medium text-charcoal underline decoration-terracotta/60 underline-offset-4 transition hover:text-terracotta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2"
    >
      {email}
    </a>
  );
}

export default function Privacy() {
  const { t } = useLang();
  const content = t.privacy_page;
  usePrivacyMetadata(content);

  return (
    <div data-testid="privacy-page" className="bg-sand-50">
      <header className="border-b border-charcoal/10 bg-sand-100 px-6 pb-16 pt-28 md:px-12 md:pb-24 md:pt-36">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.3em] text-terracotta">{content.overline}</p>
          <h1 className="mt-5 max-w-4xl font-serif-display text-5xl font-light leading-[1.02] tracking-tight text-charcoal sm:text-6xl md:text-7xl">
            {content.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-8 text-charcoal-400 md:text-lg">{content.summary}</p>
          <p className="mt-8 text-sm text-charcoal-400">{content.last_updated}</p>
        </div>
      </header>

      <article className="mx-auto max-w-5xl space-y-12 px-6 py-16 md:space-y-16 md:px-12 md:py-24">
        <PolicySection number={1} title={content.introduction.title}>
          <p className={bodyClass}>{content.introduction.body}</p>
        </PolicySection>

        <PolicySection number={2} title={content.controller.title}>
          <p className={bodyClass}>{content.controller.statement}</p>
          <address className="not-italic border-l-2 border-terracotta bg-sand-100 px-6 py-6 text-base leading-8 text-charcoal md:px-8 md:text-lg">
            <p className="font-semibold">{content.controller.name}</p>
            <p>{content.controller.role}</p>
            <p>{content.controller.location}</p>
            <p className="mt-3">
              {content.controller.email_label}: <EmailLink email={content.controller.email} />
            </p>
          </address>
        </PolicySection>

        <PolicySection number={3} title={content.information.title}>
          <p className={bodyClass}>{content.information.account}</p>
          <p className={bodyClass}>{content.information.forms}</p>
          <div>
            <p className={bodyClass}>{content.information.analytics_intro}</p>
            <BulletList items={content.information.analytics_items} />
          </div>
        </PolicySection>

        <PolicySection number={4} title={content.analytics.title}>
          <p className={bodyClass}>{content.analytics.intro}</p>
          <div>
            <p className={bodyClass}>{content.analytics.purpose_intro}</p>
            <BulletList items={content.analytics.purposes} />
          </div>
          <p className={`${bodyClass} font-medium text-charcoal`}>{content.analytics.advertising}</p>
          <div className="pt-2">
            <p className={bodyClass}>{content.analytics.retention_intro}</p>
            <dl className="mt-5 grid gap-4 sm:grid-cols-2">
              {content.analytics.retention.map((item) => (
                <div key={item.label} className="border border-charcoal/10 bg-white p-5 md:p-6">
                  <dt className="text-sm leading-6 text-charcoal-400">{item.label}</dt>
                  <dd className="mt-2 font-serif-display text-3xl text-charcoal">{item.value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-sm leading-7 text-charcoal-400">{content.analytics.retention_note}</p>
          </div>
        </PolicySection>

        <PolicySection number={5} title={content.whatsapp.title}>
          <p className={bodyClass}>{content.whatsapp.intro}</p>
          <div className="bg-charcoal px-6 py-6 text-sand-50 md:px-8 md:py-8">
            <ul className="list-disc space-y-3 pl-5 text-base leading-7 marker:text-terracotta md:text-lg">
              {content.whatsapp.limits.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </PolicySection>

        <PolicySection number={6} title={content.external_links.title}>
          <p className={bodyClass}>{content.external_links.body}</p>
        </PolicySection>

        <PolicySection number={7} title={content.cookies.title}>
          <p className={bodyClass}>{content.cookies.intro}</p>
          <p className={bodyClass}>{content.cookies.storage}</p>
        </PolicySection>

        <PolicySection number={8} title={content.withdrawal.title}>
          <p className={bodyClass}>
            {content.withdrawal.body_before} <span className="font-medium text-charcoal">“{content.withdrawal.control}”</span> {content.withdrawal.body_after}
          </p>
        </PolicySection>

        <PolicySection number={9} title={content.legal_basis.title}>
          <p className={bodyClass}>{content.legal_basis.body}</p>
        </PolicySection>

        <PolicySection number={10} title={content.sharing.title}>
          <p className={bodyClass}>{content.sharing.body}</p>
        </PolicySection>

        <PolicySection number={11} title={content.rights.title}>
          <p className={bodyClass}>{content.rights.intro}</p>
          <BulletList items={content.rights.items} />
          <p className="text-sm leading-7 text-charcoal-400">{content.rights.note}</p>
          <p className={bodyClass}>
            {content.rights.contact_before} <EmailLink email={content.rights.email} />.
          </p>
        </PolicySection>

        <PolicySection number={12} title={content.updates.title}>
          <p className={bodyClass}>{content.updates.body}</p>
          <p className="text-sm font-medium text-charcoal">{content.last_updated}</p>
        </PolicySection>
      </article>
    </div>
  );
}
